// ./src/utils/api/TireSetService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";
import { Expense } from "./ExpenseService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const TIRE_SET_ITEM_FIELDS = `
  id
  tireSetId
  expenseId
  brand
  model
  tireSize
  position
  quantity
  tireCondition
  treadDepthInitial
  dotCode
  isRegistered
  odometerAtInstall
  mileageAccumulatedPrevious
  mileageAccumulatedPreviousDisplay
  mileageSinceInstall
  mileageSinceInstallDisplay
  mileageTotal
  mileageTotalDisplay
  treadDepthCurrent
  treadDepthMeasuredAt
  warningFlags
  notes
  status
  createdAt
  updatedAt
  expense {
    id
    odometer
    whenDone
    totalPrice
    paidInCurrency
  }
`;

const TIRE_SET_FIELDS = `
  id
  accountId
  userId
  carId
  name
  tireType
  storageLocation
  quantity
  notes
  installedAt
  storedAt
  mileageWarranty
  ageLimitYears
  treadLimitMm
  warningFlags
  status
  version
  createdBy
  updatedBy
  removedBy
  createdAt
  updatedAt
  items {
    ${TIRE_SET_ITEM_FIELDS}
  }
  car {
    id
    label
    mileageIn
    driveType
  }
  expenses {
    id
    odometer
    whenDone
    totalPrice
    paidInCurrency
    kindId
    shortNote
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
`;

const TIRE_SET_SWAP_FIELDS = `
  installedSet {
    ${TIRE_SET_FIELDS}
  }
  storedSet {
    ${TIRE_SET_FIELDS}
  }
  expense {
    id
    odometer
    whenDone
    totalPrice
    paidInCurrency
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const TIRE_SET_LIST_QUERY = gql`
  query TireSetList($filter: TireSetFilter, $params: PaginationAndSorting) {
    tireSetList(filter: $filter, params: $params) {
      code
      data {
        ${TIRE_SET_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TIRE_SET_GET_QUERY = gql`
  query TireSetGet($id: ID) {
    tireSetGet(id: $id) {
      code
      data {
        ${TIRE_SET_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TIRE_SET_GET_MANY_QUERY = gql`
  query TireSetGetMany($ids: [ID]) {
    tireSetGetMany(ids: $ids) {
      code
      data {
        ${TIRE_SET_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const TIRE_SET_CREATE_MUTATION = gql`
  mutation TireSetCreate($params: TireSetInput) {
    tireSetCreate(params: $params) {
      code
      data {
        ${TIRE_SET_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TIRE_SET_UPDATE_MUTATION = gql`
  mutation TireSetUpdate($where: TireSetWhereInput, $params: TireSetInput) {
    tireSetUpdate(where: $where, params: $params) {
      code
      data {
        ${TIRE_SET_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TIRE_SET_REMOVE_MUTATION = gql`
  mutation TireSetRemove($where: TireSetWhereInput) {
    tireSetRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TIRE_SET_REMOVE_MANY_MUTATION = gql`
  mutation TireSetRemoveMany($where: [TireSetWhereInput]) {
    tireSetRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TIRE_SET_SWAP_MUTATION = gql`
  mutation TireSetSwap($params: TireSetSwapInput) {
    tireSetSwap(params: $params) {
      code
      data {
        ${TIRE_SET_SWAP_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Constants
// =============================================================================

/**
 * Tire type constants
 */
export const TIRE_TYPE = {
  SUMMER: 'summer',
  WINTER: 'winter',
  ALL_SEASON: 'all_season',
  ALL_WEATHER: 'all_weather',
  PERFORMANCE: 'performance',
  OFF_ROAD: 'off_road',
} as const;

export type TireTypeValue = (typeof TIRE_TYPE)[keyof typeof TIRE_TYPE];

/**
 * Tire set status constants
 */
export const TIRE_SET_STATUS = {
  ACTIVE: 100,
  STORED: 300,
  RETIRED: 5000,
} as const;

export type TireSetStatusValue = (typeof TIRE_SET_STATUS)[keyof typeof TIRE_SET_STATUS];

/**
 * Tire position constants
 */
export const TIRE_POSITION = {
  ALL: 'all',
  FRONT: 'front',
  REAR: 'rear',
} as const;

export type TirePositionValue = (typeof TIRE_POSITION)[keyof typeof TIRE_POSITION];

/**
 * Tire condition constants
 */
export const TIRE_CONDITION = {
  NEW: 'new',
  USED: 'used',
  CAME_WITH_VEHICLE: 'came_with_vehicle',
} as const;

export type TireConditionValue = (typeof TIRE_CONDITION)[keyof typeof TIRE_CONDITION];

/**
 * Tire-related expense kind IDs
 */
export const TIRE_EXPENSE_KIND = {
  TIRE_REPAIR_REPLACEMENT: 213,
  SEASONAL_TIRE_SERVICE: 22,
  TIRE_BALANCE: 23,
  TIRE_ROTATION: 24,
} as const;

/**
 * Warning flag bitmask constants, computed by daily cron job on the backend.
 */
export const TIRE_WARNING_FLAGS = {
  AGE_WARNING: 1,    // bit 0 — tire age ≥ 70% of age limit
  AGE_CRITICAL: 2,    // bit 1 — tire age ≥ 100% of age limit
  MILEAGE_WARNING: 4,    // bit 2 — mileage ≥ 70% of warranty
  MILEAGE_CRITICAL: 8,    // bit 3 — mileage ≥ 100% of warranty
  TREAD_WARNING: 16,   // bit 4 — tread depth ≤ 130% of limit
  TREAD_CRITICAL: 32,   // bit 5 — tread depth ≤ 100% of limit
  SEASONAL_MISMATCH: 64,   // bit 6 — wrong tire type for current season (set-level only)
  STORAGE_LONG: 128,  // bit 7 — stored too long (set-level only)
  TREAD_STALE: 256,  // bit 8 — tread measurement outdated (item-level only)
} as const;

/**
 * Combination masks for quick severity checks
 */
const CRITICAL_MASK =
  TIRE_WARNING_FLAGS.AGE_CRITICAL |
  TIRE_WARNING_FLAGS.MILEAGE_CRITICAL |
  TIRE_WARNING_FLAGS.TREAD_CRITICAL;

const WARNING_MASK =
  TIRE_WARNING_FLAGS.AGE_WARNING |
  TIRE_WARNING_FLAGS.MILEAGE_WARNING |
  TIRE_WARNING_FLAGS.TREAD_WARNING |
  TIRE_WARNING_FLAGS.SEASONAL_MISMATCH |
  TIRE_WARNING_FLAGS.STORAGE_LONG |
  TIRE_WARNING_FLAGS.TREAD_STALE;

/**
 * Default warning thresholds (used when the set's value is null)
 */
export const TIRE_WARNING_DEFAULTS = {
  MILEAGE_WARRANTY_KM: 80_000,
  AGE_LIMIT_YEARS: 10,
  TREAD_LIMIT_MM: 2.0,
} as const;

// =============================================================================
// Types
// =============================================================================

export interface TireSetItem {
  id?: string | undefined;
  tireSetId?: string | undefined;
  expenseId?: string | undefined;
  /** Tire manufacturer name */
  brand?: string | undefined;
  /** Tire model name */
  model?: string | undefined;
  /** Tire size code (e.g., 225/45R17) */
  tireSize?: string | undefined;
  /** Position on vehicle: all, front, rear */
  position?: TirePositionValue | undefined;
  /** Number of tires of this type in the set */
  quantity?: number | undefined;
  /** Condition when acquired: new, used, came_with_vehicle */
  tireCondition?: TireConditionValue | undefined;
  /** Starting tread depth in mm */
  treadDepthInitial?: number | undefined;
  /** DOT manufacturing date code */
  dotCode?: string | undefined;
  /** Whether tires are registered with manufacturer */
  isRegistered?: boolean | undefined;
  /** Odometer reading when last installed, in car's mileageIn unit. Null when stored. */
  odometerAtInstall?: number | undefined;
  /**
   * Frozen historical mileage from all previous installation periods (swap-out cycles).
   * Does not include the current installation. In car's mileageIn unit.
   */
  mileageAccumulatedPrevious?: number | undefined;
  /** Same as mileageAccumulatedPrevious but in user's distanceIn unit */
  mileageAccumulatedPreviousDisplay?: number | undefined;
  /**
   * Live mileage driven during the current installation period.
   * Null when stored (not currently installed). Zero if installed but hasn't moved.
   * In car's mileageIn unit.
   */
  mileageSinceInstall?: number | undefined;
  /** Same as mileageSinceInstall but in user's distanceIn unit. Null when stored. */
  mileageSinceInstallDisplay?: number | undefined;
  /**
   * Total lifetime mileage: mileageAccumulatedPrevious + mileageSinceInstall.
   * Always available (for stored tires, equals mileageAccumulatedPrevious).
   * In car's mileageIn unit.
   */
  mileageTotal?: number | undefined;
  /** Same as mileageTotal but in user's distanceIn unit */
  mileageTotalDisplay?: number | undefined;
  /** Latest measured tread depth in mm */
  treadDepthCurrent?: number | undefined;
  /** When tread depth was last measured */
  treadDepthMeasuredAt?: string | undefined;
  /** Bitmask of active warnings for this item */
  warningFlags?: number | undefined;
  notes?: string | undefined;
  status?: number | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  /** Linked purchase expense */
  expense?: Expense | undefined;
}

export interface TireSet {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  carId?: string | undefined;
  /** User-friendly label (e.g., Summer 2024, Winter Nokians) */
  name?: string | undefined;
  /** Tire type: summer, winter, all_season, all_weather, performance, off_road */
  tireType?: TireTypeValue | undefined;
  /** Where tires are stored when not on vehicle */
  storageLocation?: string | undefined;
  /** Total number of tires in the set */
  quantity?: number | undefined;
  notes?: string | undefined;
  /** When the tire set was last installed on the vehicle */
  installedAt?: string | undefined;
  /** When the tire set was last placed in storage */
  storedAt?: string | undefined;
  /** Mileage warranty in user's preferred distance units (null = use global default) */
  mileageWarranty?: number | undefined;
  /** Max tire age in years (null = use global default) */
  ageLimitYears?: number | undefined;
  /** Min tread depth in mm (null = use global default) */
  treadLimitMm?: number | undefined;
  /** Bitmask of active warnings for this set */
  warningFlags?: number | undefined;
  status?: TireSetStatusValue | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  /** Tire items within this set */
  items?: TireSetItem[] | undefined;
  /** Vehicle this tire set belongs to */
  car?: Car | undefined;
  /** Service expenses linked to this set */
  expenses?: Expense[] | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface TireSetItemInput {
  /** Item ID (required for updates, omit for new items) */
  id?: string | null | undefined;
  brand?: string | null | undefined;
  model?: string | null | undefined;
  tireSize?: string | null | undefined;
  position?: TirePositionValue | null | undefined;
  quantity?: number | null | undefined;
  tireCondition?: TireConditionValue | null | undefined;
  treadDepthInitial?: number | null | undefined;
  dotCode?: string | null | undefined;
  isRegistered?: boolean | null | undefined;
  /** Existing expense ID to link as purchase record */
  expenseId?: string | null | undefined;
  /** Initial accumulated mileage for used tires, in car's mileageIn unit */
  mileageAccumulated?: number | null | undefined;
  /** Current tread depth measurement in mm */
  treadDepthCurrent?: number | null | undefined;
  notes?: string | null | undefined;
  /** Use 10000 to remove an item */
  status?: number | null | undefined;
}

export interface TireSetInput {
  carId?: string | null | undefined;
  name?: string | null | undefined;
  tireType?: string | null | undefined;
  storageLocation?: string | null | undefined;
  quantity?: number | null | undefined;
  notes?: string | null | undefined;
  status?: number | null | undefined;
  /** Mileage warranty in users' preferred distance units (null = use global default 80,000 km) */
  mileageWarranty?: number | null | undefined;
  /** Max tire age in years (null = use global default 10 years) */
  ageLimitYears?: number | null | undefined;
  /** Min tread depth in mm (null = use global default 2.0 mm) */
  treadLimitMm?: number | null | undefined;
  /** Tire items in this set */
  items?: TireSetItemInput[] | null | undefined;
  /** Whether to create an installation expense */
  createExpense?: boolean | null | undefined;
  /** Installation/purchase expense details (standard ExpenseInput) */
  expenseDetails?: any | null | undefined;
}

export interface TireSetSwapInput {
  /** Vehicle ID */
  carId: string;
  /** ID of the stored tire set to install */
  installTireSetId: string;
  /** Where to store the outgoing set */
  storageLocation?: string | null | undefined;
  /** Swap expense details (required). odometer inside is required for mileage tracking. */
  expenseDetails: any;
}

export interface TireSetSwapData {
  /** The tire set that was installed (now active) */
  installedSet?: TireSet | undefined;
  /** The tire set that was removed (now stored) */
  storedSet?: TireSet | undefined;
  /** The swap expense record */
  expense?: Expense | undefined;
}

export interface TireSetFilter {
  id?: string[] | null | undefined;
  carId?: string[] | null | undefined;
  tireType?: TireTypeValue[] | null | undefined;
  searchKeyword?: string | null | undefined;
  status?: number[] | null | undefined;
  /** If true, return only sets with warnings; if false, only clean sets */
  hasWarnings?: boolean | null | undefined;
  /** Bitmask filter: returns sets where (warning_flags & value) > 0 */
  warningFlags?: number | null | undefined;
}

export interface TireSetWhereInput {
  id?: string | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a specific warning flag is set in the bitmask
 */
export function hasFlag(flags: number | null | undefined, flag: number): boolean {
  if (!flags) return false;
  return (flags & flag) > 0;
}

/**
 * Check if any critical warning is present (age, mileage, or tread critical)
 */
export function hasCritical(flags: number | null | undefined): boolean {
  if (!flags) return false;
  return (flags & CRITICAL_MASK) > 0;
}

/**
 * Check if any non-critical warning is present
 */
export function hasWarning(flags: number | null | undefined): boolean {
  if (!flags) return false;
  return (flags & WARNING_MASK) > 0;
}

/**
 * Check if any warning or critical flag is set
 */
export function hasAnyWarning(flags: number | null | undefined): boolean {
  return hasCritical(flags) || hasWarning(flags);
}

/**
 * Get warning severity: 'critical', 'warning', or null
 */
export function getWarningSeverity(flags: number | null | undefined): 'critical' | 'warning' | null {
  if (hasCritical(flags)) return 'critical';
  if (hasWarning(flags)) return 'warning';
  return null;
}

/**
 * Decode warning flags bitmask into an array of active warning keys.
 * Returns keys from TIRE_WARNING_FLAGS that are set in the bitmask.
 */
export function decodeWarningFlags(flags: number | null | undefined): string[] {
  if (!flags) return [];
  return Object.entries(TIRE_WARNING_FLAGS)
    .filter(([, bit]) => (flags & bit) > 0)
    .map(([key]) => key);
}

/**
 * Parse DOT code to extract manufacturing date.
 * DOT code last 4 digits: WWYY (week, year)
 * e.g., "2319" = week 23 of 2019
 *
 * @returns Object with week and year, or null if invalid
 */
export function parseDotCode(dotCode: string | null | undefined): { week: number; year: number } | null {
  if (!dotCode || dotCode.length < 4) return null;

  const last4 = dotCode.slice(-4);
  const week = parseInt(last4.substring(0, 2), 10);
  const year = parseInt(last4.substring(2, 4), 10) + 2000;

  if (isNaN(week) || isNaN(year) || week < 1 || week > 53) return null;

  return { week, year };
}

/**
 * Get a summary description of a tire set's items
 * e.g., "4× Michelin Pilot Sport 4 225/45R17" or "2× Michelin (front) + 2× Continental (rear)"
 */
export function getTireSetSummary(items: TireSetItem[] | undefined): string {
  if (!items || items.length === 0) return 'No tires';

  if (items.length === 1) {
    const item = items[0];
    const parts = [`${item.quantity}×`, item.brand, item.model, item.tireSize].filter(Boolean);
    return parts.join(' ');
  }

  return items
    .map((item) => {
      const parts = [`${item.quantity}×`, item.brand];
      if (item.position && item.position !== 'all') {
        parts.push(`(${item.position})`);
      }
      return parts.filter(Boolean).join(' ');
    })
    .join(' + ');
}

/**
 * Get the effective threshold value, falling back to the global default if null.
 */
export function getEffectiveThreshold(
  value: number | null | undefined,
  defaultValue: number
): number {
  return value != null ? value : defaultValue;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * Tire set service that handles all tire set-related API calls.
 * Supports CRUD operations and tire swap workflow.
 */
export class TireSetService {
  /**
   * Get a list of tire sets with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: TireSetFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(TIRE_SET_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single tire set by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(TIRE_SET_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple tire sets by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(TIRE_SET_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new tire set with items.
   * If createExpense is true, also creates an installation expense.
   */
  static async create(params: TireSetInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(TIRE_SET_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing tire set and its items.
   * Items with id are updated, items without id are created,
   * items with status=10000 are soft-deleted.
   */
  static async update(
    where: TireSetWhereInput,
    params: TireSetInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(TIRE_SET_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a tire set and its items (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(TIRE_SET_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple tire sets (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(TIRE_SET_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }

  /**
   * Swap tire sets: install a stored set, move the current active set to storage.
   * Always creates a swap expense. expenseDetails.odometer is required for mileage tracking.
   */
  static async swap(params: TireSetSwapInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(TIRE_SET_SWAP_MUTATION, {
      params,
    });
    return result;
  }
}